import {
  Component,
  OnInit,
  OnChanges,
  SimpleChanges,
  Input,
  Output,
  EventEmitter,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import {
  CustomizedSidebarService,
  CustomizedSidebarConfig,
  SidebarSection,
  SidebarQuickLink,
} from '@core/services/customized-sidebar.service';
import { extractSidebarConstraints } from '@core/services/my-sidebar.service';
import { ToastService } from '@core/services/toaster';
import { Auth } from '@core/auth/auth';
import { Dropdown } from '@shared/components/forms-component/dropdown/dropdown';

@Component({
  selector: 'app-customized-sidebar',
  standalone: true,
  imports: [CommonModule, FormsModule, Dropdown],
  templateUrl: './customized-sidebar.component.html',
  styleUrl: './customized-sidebar.component.scss',
})
export class CustomizedSidebarComponent implements OnInit, OnChanges {
  private readonly sidebarCustomService = inject(CustomizedSidebarService);
  private readonly toaster = inject(ToastService);
  private readonly auth = inject(Auth);

  @Input() isOpen = false;
  @Output() close = new EventEmitter<void>();
  @Output() save = new EventEmitter<CustomizedSidebarConfig>();

  activeTab: 'home' | 'sections' = 'sections';

  config: CustomizedSidebarConfig = { sections: [] };

  // New Section modal state
  showNewSectionModal = false;
  newSectionName = '';

  // New Field modal state
  showNewFieldModal = false;
  targetSectionIdForNewField: string | null = null;
  selectedFieldId = '';

  availableSystemMenus: { id: string; name: string; link: string; icon: string; menu_level?: string; displayName?: string }[] = [];

  // Loading & Saving states
  isLoading = false;
  isSaving = false;
  isResetting = false;

  // Drag & drop tracking variables for Sections
  draggedSectionIndex: number | null = null;

  // Drag & drop tracking variables for Items
  draggedItemSectionId: string | null = null;
  draggedItemIndex: number | null = null;

  ngOnInit(): void {
    this.loadConfig();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isOpen'] && changes['isOpen'].currentValue === true) {
      this.loadConfig();
    }
  }

  private mapSystemMenus(): { id: string; name: string; link: string; icon: string; menu_level?: string; displayName: string }[] {
    return this.sidebarCustomService
      .getAvailableSystemMenus()
      .filter((m) => m.menu_level === 'Secondary' || m.menu_level === 'Tertiary')
      .map((m) => ({
        ...m,
        displayName: m.name + (m.menu_level ? ` (${m.menu_level})` : ''),
      }));
  }

  loadConfig(): void {
    this.isLoading = true;
    this.sidebarCustomService.fetchAvailableMenus().subscribe({
      next: () => {
        this.availableSystemMenus = this.mapSystemMenus();
        this.sidebarCustomService.loadSavedArrangement().subscribe({
          next: (savedConfig) => {
            this.config = JSON.parse(JSON.stringify(savedConfig));
            this.isLoading = false;
          },
          error: () => {
            this.config = JSON.parse(JSON.stringify(this.sidebarCustomService.getConfig()));
            this.isLoading = false;
          },
        });
      },
      error: () => {
        this.availableSystemMenus = this.mapSystemMenus();
        this.config = JSON.parse(JSON.stringify(this.sidebarCustomService.getConfig()));
        this.isLoading = false;
      },
    });
  }

  setActiveTab(tab: 'home' | 'sections'): void {
    this.activeTab = tab;
  }

  // --- SECTION ACTIONS ---
  openNewSectionModal(): void {
    this.newSectionName = '';
    this.showNewSectionModal = true;
  }

  closeNewSectionModal(): void {
    this.showNewSectionModal = false;
    this.newSectionName = '';
  }

  addSection(): void {
    const name = this.newSectionName.trim();
    if (!name) {
      this.toaster.error('Section name is required.');
      return;
    }
    if (name.length < 3 || name.length > 50) {
      this.toaster.error('Section name must be between 3 and 50 characters.');
      return;
    }
    const labelPattern = /^(?=.*[A-Za-z0-9])[A-Za-z0-9 _&-]+$/;
    if (!labelPattern.test(name)) {
      this.toaster.error(
        'Section name may contain only letters, numbers, spaces, hyphens, underscores and ampersands.'
      );
      return;
    }

    const isDuplicate = this.config.sections.some(
      (s) => s.name.trim().toLowerCase() === name.toLowerCase()
    );
    if (isDuplicate) {
      this.toaster.error('A section with this name already exists.');
      return;
    }

    const id = 'sec_' + Date.now();
    const newSec: SidebarSection = {
      id,
      name,
      enabled: true,
      order: this.config.sections.length + 1,
      items: [],
    };

    this.config.sections.push(newSec);
    this.closeNewSectionModal();
  }

  toggleSection(section: SidebarSection): void {
    section.enabled = !section.enabled;
  }

  // Section Drag and Drop Reordering
  onSectionDragStart(index: number, event: DragEvent): void {
    this.draggedSectionIndex = index;
    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = 'move';
    }
  }

  onSectionDragOver(event: DragEvent): void {
    event.preventDefault();
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'move';
    }
  }

  onSectionDrop(targetIndex: number, event: DragEvent): void {
    event.preventDefault();
    if (this.draggedSectionIndex === null || this.draggedSectionIndex === targetIndex) return;

    const draggedSec = this.config.sections.splice(this.draggedSectionIndex, 1)[0];
    this.config.sections.splice(targetIndex, 0, draggedSec);
    this.draggedSectionIndex = null;
  }

  // --- FIELD / ITEM ACTIONS ---
  openNewFieldModal(sectionId: string): void {
    this.targetSectionIdForNewField = sectionId;
    this.selectedFieldId = '';
    this.showNewFieldModal = true;
  }

  closeNewFieldModal(): void {
    this.showNewFieldModal = false;
    this.targetSectionIdForNewField = null;
    this.selectedFieldId = '';
  }

  addField(): void {
    if (!this.targetSectionIdForNewField || !this.selectedFieldId) return;

    const section = this.config.sections.find((s) => s.id === this.targetSectionIdForNewField);
    const menuDef = this.availableSystemMenus.find((m) => String(m.id) === String(this.selectedFieldId));

    if (section && menuDef) {
      const isDuplicate = section.items.some(
        (i) => String(i.menu_id) === String(menuDef.id) || String(i.id) === String(menuDef.id)
      );
      if (isDuplicate) {
        this.toaster.error('This menu item is already pinned in this section.');
        return;
      }

      if (section.items.length >= 20) {
        this.toaster.error('A section cannot contain more than 20 items.');
        return;
      }

      const newItem: SidebarQuickLink = {
        id: 'item_' + menuDef.id + '_' + Date.now(),
        name: menuDef.name,
        link: menuDef.link,
        icon: menuDef.icon,
        enabled: true,
        order: section.items.length + 1,
        menu_id: Number(menuDef.id) || null,
        menu_level: menuDef.menu_level || 'Primary',
        item_type: 'MENU',
      };
      section.items.push(newItem);
    }

    this.closeNewFieldModal();
  }

  toggleItem(item: SidebarQuickLink): void {
    item.enabled = !item.enabled;
  }

  // Item Drag and Drop Reordering
  onItemDragStart(sectionId: string, index: number, event: DragEvent): void {
    this.draggedItemSectionId = sectionId;
    this.draggedItemIndex = index;
    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = 'move';
    }
  }

  onItemDragOver(event: DragEvent): void {
    event.preventDefault();
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'move';
    }
  }

  onItemDrop(sectionId: string, targetIndex: number, event: DragEvent): void {
    event.preventDefault();
    if (
      this.draggedItemSectionId !== sectionId ||
      this.draggedItemIndex === null ||
      this.draggedItemIndex === targetIndex
    ) {
      return;
    }

    const section = this.config.sections.find((s) => s.id === sectionId);
    if (!section) return;

    const draggedItem = section.items.splice(this.draggedItemIndex, 1)[0];
    section.items.splice(targetIndex, 0, draggedItem);
    this.draggedItemSectionId = null;
    this.draggedItemIndex = null;
  }

  // --- MODAL CONTROLS ---
  onCancel(): void {
    this.loadConfig(); // Reset draft changes
    this.close.emit();
  }

  onSave(): void {
    if (this.isSaving) return;
    this.isSaving = true;

    this.sidebarCustomService.saveConfigToServer(this.config).subscribe({
      next: (res) => {
        this.isSaving = false;
        this.toaster.success(res?.message || 'Custom sidebar saved successfully!');
        this.save.emit(this.config);
        this.close.emit();
      },
      error: (err) => {
        this.isSaving = false;
        const { message, constraints } = extractSidebarConstraints(err);
        const displayMsg = constraints[0]?.message || message || 'Failed to save sidebar configuration.';
        this.toaster.error(displayMsg);
      },
    });
  }

  onReset(): void {
    if (this.isResetting) return;
    this.isResetting = true;

    this.sidebarCustomService.resetServerConfig().subscribe({
      next: (res) => {
        this.isResetting = false;
        this.toaster.success(res?.message || 'Custom sidebar reset to default successfully!');
        this.config = { sections: [] };
        this.save.emit(this.config);
        this.close.emit();
      },
      error: (err) => {
        this.isResetting = false;
        this.toaster.error(err?.error?.message || 'Failed to reset sidebar.');
      },
    });
  }
}
